import type { SessionListing, SessionResponse } from "@meleeroyale/protocol";
import { useSyncExternalStore } from "react";
import { Controls } from "../input/controls.ts";
import { GameClient } from "../net/game-client.ts";

/** A seat in a room, plus the PIN this player typed or chose (shown in the waiting room). */
export interface Session extends SessionResponse {
  pin: string | null;
}

export type Screen =
  | { name: "lobby"; notice: string | null }
  | { name: "match"; session: Session; nickname: string }
  | { name: "results"; session: Session; nickname: string; client: GameClient };

let screen: Screen = { name: "lobby", notice: null };
const listeners = new Set<() => void>();

function set(next: Screen): void {
  screen = next;
  for (const l of listeners) l();
}

export function useScreen(): Screen {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => screen,
  );
}

const NICK_KEY = "meleeroyale.nickname";

export function loadNickname(): string {
  try {
    return localStorage.getItem(NICK_KEY) ?? "";
  } catch {
    return "";
  }
}

function saveNickname(nickname: string): void {
  try {
    localStorage.setItem(NICK_KEY, nickname);
  } catch {
    /* private mode: the name just will not be remembered */
  }
}

/**
 * Asks the edge for a seat and, on success, moves to the match screen.
 * Resolves to an error code (see `t.lobby.errors`) or null.
 */
async function enter(
  path: string,
  body: Record<string, unknown>,
  nickname: string,
  pin: string | null,
): Promise<string | null> {
  try {
    const res = await fetch(path, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = (await res.json().catch(() => ({}))) as { error?: string };
      return err.error ?? "generic";
    }
    const session = (await res.json()) as SessionResponse;
    saveNickname(nickname);
    set({ name: "match", session: { ...session, pin }, nickname });
    return null;
  } catch {
    return "generic";
  }
}

export function startPractice(nickname: string, bots: number): Promise<string | null> {
  return enter("/api/practice", { nickname, bots }, nickname, null);
}

export function createSession(
  nickname: string,
  minHumans: number,
  bots: number,
  pin: string | null,
): Promise<string | null> {
  return enter(
    "/api/sessions",
    { nickname, minHumans, bots, ...(pin ? { pin } : {}) },
    nickname,
    pin,
  );
}

export function joinSession(
  nickname: string,
  roomId: string,
  pin: string | null,
): Promise<string | null> {
  return enter(
    `/api/sessions/${encodeURIComponent(roomId)}/join`,
    { nickname, ...(pin ? { pin } : {}) },
    nickname,
    pin,
  );
}

export async function listSessions(): Promise<SessionListing[]> {
  const res = await fetch("/api/sessions");
  if (!res.ok) throw new Error(`sessions: ${res.status}`);
  const body = (await res.json()) as { sessions?: SessionListing[] };
  return body.sessions ?? [];
}

export function matchEnded(session: Session, nickname: string, client: GameClient): void {
  set({ name: "results", session, nickname, client });
}

/** Back to the menu, optionally with a notice code (see `t.lobby.errors`). */
export function backToLobby(notice: string | null = null): void {
  set({ name: "lobby", notice });
}

export function wsUrlFor(wsPath: string): string {
  const proto = location.protocol === "https:" ? "wss" : "ws";
  return `${proto}://${location.host}${wsPath}`;
}

export { Controls, GameClient };
